import { useCallback, useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"

// Optimistic state + debounced sparse-PATCH dispatcher for quarter_rocks.
//
// Usage:
//   const { rocks, updateRock, appendRock, appendRockBusy, error, clearError } = useRockUpdate(
//     currentQuarter.id,
//     currentQuarter.rocks,
//   )
//
// `rocks` is the local optimistic copy — render the grid from this, not
// from the server prop. `updateRock(rockId, field, value)` applies the
// edit locally immediately (so the slider/checkbox/pill feels responsive)
// and PATCHes the server in the background. progress_pct writes are
// debounced so slider drags collapse into a single PATCH.
//
// Uses plain fetch (not Inertia's router.patch) because the sparse-PATCH
// endpoint replies with bare JSON on error, which Inertia's onError
// callback does NOT receive (it routes through the global `invalid`
// event instead). fetch lets us parse { error } responses directly,
// cancel in-flight requests on rapid edits, and guard against post-
// unmount state mutation.
//
// Server contract:
//   PATCH /app/planning/quarterly_check_ins/:id
//   body: { rock_update: { id, field, value } }  # sparse field edit
//      or { rocks: [ ... ] }  # replace whole quarter_rocks JSON (add/reorder)
//   response: 204 on success; 4xx with { error: <string> } on failure.
//
// On error (422, 409, 5xx, network) the field reverts to the last
// server-confirmed value and `error` surfaces. `updateRock` returns a
// Promise so callers (like RockFinalizeModal) can await confirmation
// before advancing their own state.
export function useRockUpdate(checkInId, initialRocks = [], { debounceMs = 250 } = {}) {
  const [rocks, setRocks] = useState(() => deepCopy(initialRocks))
  const [error, setError] = useState(null)
  const [appendRockBusy, setAppendRockBusy] = useState(false)
  const confirmedRef = useRef(deepCopy(initialRocks))
  const timersRef = useRef(new Map()) // key = `${rockId}:${field}` -> timeout id
  const controllersRef = useRef(new Map()) // key = `${rockId}:${field}` -> AbortController
  const mountedRef = useRef(true)
  const prevCheckInIdRef = useRef(checkInId)

  // Reset local state when the server prop changes (Inertia navigation /
  // partial reload). Only cancel in-flight PATCHes when the quarterly
  // check-in id changes; otherwise RockFinalizeModal's result PATCH can
  // be aborted mid-flight when props tick and stringify(initialRocks)
  // changes shape slightly, leaving the modal stuck.
  useEffect(() => {
    const snapshot = deepCopy(initialRocks)
    const checkInChanged = prevCheckInIdRef.current !== checkInId
    if (checkInChanged) {
      prevCheckInIdRef.current = checkInId
      timersRef.current.forEach((t) => clearTimeout(t))
      timersRef.current.clear()
      controllersRef.current.forEach((c) => c.abort())
      controllersRef.current.clear()
    }
    confirmedRef.current = snapshot
    setRocks(snapshot)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInId, JSON.stringify(initialRocks)])

  useEffect(() => {
    mountedRef.current = true
    const timers = timersRef.current
    const controllers = controllersRef.current
    return () => {
      mountedRef.current = false
      timers.forEach((t) => clearTimeout(t))
      timers.clear()
      controllers.forEach((c) => c.abort())
      controllers.clear()
    }
  }, [])

  const applyLocal = useCallback((rockId, field, value) => {
    setRocks((prev) =>
      prev.map((r) =>
        rockIdEq(r.id, rockId)
          ? { ...r, [camelField(field)]: value, updatedAt: new Date().toISOString() }
          : r,
      ),
    )
  }, [])

  const revertField = useCallback((rockId, field) => {
    setRocks((prev) => {
      const confirmed = confirmedRef.current.find((r) => rockIdEq(r.id, rockId))
      if (!confirmed) return prev
      return prev.map((r) =>
        rockIdEq(r.id, rockId) ? { ...r, [camelField(field)]: confirmed[camelField(field)] } : r,
      )
    })
  }, [])

  const dispatch = useCallback(
    async (rockId, field, value) => {
      if (!checkInId) return false
      const idStr = String(rockId)
      const key = `${idStr}:${field}`
      // Cancel any in-flight request for the same (rock, field) — last
      // write wins for a given target.
      const prev = controllersRef.current.get(key)
      if (prev) prev.abort()
      const ctrl = new AbortController()
      controllersRef.current.set(key, ctrl)

      try {
        const res = await fetch(`/app/planning/quarterly_check_ins/${checkInId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
            Accept: "application/json, text/html",
          },
          body: JSON.stringify({ rock_update: { id: idStr, field, value } }),
          credentials: "same-origin",
          signal: ctrl.signal,
        })
        if (!mountedRef.current) return false
        if (!res.ok) {
          let msg = "Couldn't save. Try again."
          try {
            const body = await res.json()
            if (body?.error) msg = body.error
          } catch {
            // Non-JSON body — keep default message.
          }
          setError(msg)
          revertField(rockId, field)
          return false
        }
        // Promote to confirmed state and sync visible rocks (applyLocal alone
        // can be overwritten if a prop-sync effect runs before we finish).
        confirmedRef.current = confirmedRef.current.map((r) =>
          rockIdEq(r.id, rockId)
            ? { ...r, [camelField(field)]: value, updatedAt: new Date().toISOString() }
            : r,
        )
        setRocks(deepCopy(confirmedRef.current))
        return true
      } catch (e) {
        if (e?.name === "AbortError") return false
        if (!mountedRef.current) return false
        setError("Network error — try again.")
        revertField(rockId, field)
        return false
      } finally {
        if (controllersRef.current.get(key) === ctrl) {
          controllersRef.current.delete(key)
        }
      }
    },
    [checkInId, revertField],
  )

  const updateRock = useCallback(
    (rockId, field, value) => {
      setError(null)
      applyLocal(rockId, field, value)

      const key = `${String(rockId)}:${field}`
      const timers = timersRef.current

      if (field === "progress_pct") {
        // Slider drags: debounce and resolve the Promise once the eventual
        // PATCH completes. Intermediate updates don't need individual
        // promises — only the final tick fires a PATCH.
        if (timers.has(key)) clearTimeout(timers.get(key))
        return new Promise((resolve) => {
          timers.set(
            key,
            setTimeout(() => {
              timers.delete(key)
              dispatch(rockId, field, value).then(resolve)
            }, debounceMs),
          )
        })
      }

      // status, milestones, title, result — commit immediately.
      return dispatch(rockId, field, value)
    },
    [applyLocal, dispatch, debounceMs],
  )

  const appendRock = useCallback(async () => {
    if (!checkInId) return false
    if (confirmedRef.current.length >= 5) {
      setError("You can have at most five quarter rocks.")
      return false
    }
    setError(null)
    setAppendRockBusy(true)
    const now = new Date().toISOString()
    const newRock = {
      id: crypto.randomUUID(),
      title: "New quarter rock",
      status: "on_track",
      progressPct: 0,
      milestones: [],
      createdAt: now,
      updatedAt: now,
      result: null,
    }
    const next = [...deepCopy(confirmedRef.current), newRock]
    try {
      const res = await fetch(`/app/planning/quarterly_check_ins/${checkInId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json, text/html",
        },
        body: JSON.stringify({ rocks: rocksForFullPatch(next) }),
        credentials: "same-origin",
      })
      if (!mountedRef.current) return false
      if (!res.ok) {
        let msg = "Couldn't add rock. Try again."
        try {
          const body = await res.json()
          if (body?.error) msg = body.error
        } catch {
          // keep default
        }
        setError(msg)
        setAppendRockBusy(false)
        return false
      }
      // Inertia partial reload doesn't unmount this hook, so the busy
      // flag must be cleared explicitly — otherwise the "Add a rock"
      // button stays disabled forever after the first successful add.
      setAppendRockBusy(false)
      router.reload({ preserveScroll: true })
      return true
    } catch (e) {
      if (!mountedRef.current) return false
      setError("Network error — try again.")
      setAppendRockBusy(false)
      return false
    }
  }, [checkInId])

  // Delete a rock: re-send the full quarter_rocks array minus the target
  // element (there's no per-rock DELETE route — the whole array is the unit
  // of truth). Reuses the same full-array PATCH shape as appendRock.
  const removeRock = useCallback(
    async (rockId) => {
      if (!checkInId) return false
      setError(null)
      const next = deepCopy(confirmedRef.current).filter((r) => !rockIdEq(r.id, rockId))
      try {
        const res = await fetch(`/app/planning/quarterly_check_ins/${checkInId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken(),
            Accept: "application/json, text/html",
          },
          body: JSON.stringify({ rocks: rocksForFullPatch(next) }),
          credentials: "same-origin",
        })
        if (!mountedRef.current) return false
        if (!res.ok) {
          let msg = "Couldn't delete rock. Try again."
          try {
            const body = await res.json()
            if (body?.error) msg = body.error
          } catch {
            // keep default
          }
          setError(msg)
          return false
        }
        router.reload({ preserveScroll: true })
        return true
      } catch (e) {
        if (!mountedRef.current) return false
        setError("Network error — try again.")
        return false
      }
    },
    [checkInId],
  )

  return {
    rocks,
    updateRock,
    appendRock,
    appendRockBusy,
    removeRock,
    error,
    clearError: () => setError(null),
  }
}

function deepCopy(v) {
  return JSON.parse(JSON.stringify(v))
}

function rockIdEq(a, b) {
  return String(a) === String(b)
}

function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]')
  return el ? el.getAttribute("content") : ""
}

// Full-array PATCH: translate React rock shape → JSONB keys permit_rock_array expects.
function rocksForFullPatch(rocks) {
  return rocks.map((r) => ({
    id: r.id,
    title: r.title,
    status: r.status || "on_track",
    progress_pct: Number(r.progressPct ?? 0),
    milestones: Array.isArray(r.milestones)
      ? r.milestones.map((m) => ({
          id: m.id,
          label: m.label,
          done: !!m.done,
        }))
      : [],
    created_at: r.createdAt || undefined,
    updated_at: r.updatedAt || undefined,
    result: r.result ?? null,
  }))
}

// Rock fields on the React side are camelCase (progressPct, updatedAt),
// but the PATCH field names are snake_case (progress_pct). Translate.
function camelField(snake) {
  switch (snake) {
    case "progress_pct":
      return "progressPct"
    case "updated_at":
      return "updatedAt"
    case "created_at":
      return "createdAt"
    default:
      return snake
  }
}
